import { g, layers, LayerClass } from '@hcie/core';
import { historyManager, DrawAction } from '@hcie/core';
import { renderLayers, updateLayerPanel } from '@hcie/canvas-ui';
import { api } from '@hcie/io';
import { 
    saveProjectToDataString, 
    importProjectBuffer, 
    exportProjectBuffer 
} from '@hcie/io';
import { newDocument, ImageDocument, switchDocument } from '@hcie/core';
import * as filters from '@hcie/canvas-ui';
import { zoomIn as actualZoomIn, zoomOut as actualZoomOut } from '@hcie/canvas-ui';

/**
 * Menu Handlers
 * This module provides global functions that are called by onclick attributes in index.html.
 * It bridges the modular TypeScript codebase with the legacy HTML-based UI.
 */

// --- File Operations ---

export async function handleOpenFile() {
    try {
        const filePath = await api.openFile();
        if (!filePath) return;

        console.log('[handleOpenFile] File selected:', filePath);
        
        const buffer = await api.readFileBinary(filePath);
        if (!buffer) {
            alert('Could not read file binary data.');
            return;
        }

        console.log('[handleOpenFile] Binary data ready. İçe aktarılıyor...');
        await importImageFromBuffer(buffer.buffer as ArrayBuffer, filePath.split('/').pop() || "Untitled", filePath);
    } catch (err) {
        console.error('[handleOpenFile] Hata:', err);
        alert(`Dosya açılamadı: ${(err as Error).message}`);
    }
}

/**
 * Robust handler to load an image/project from an ArrayBuffer.
 * Part of the Platform Bridge architecture.
 */
export async function importImageFromBuffer(buffer: ArrayBuffer, name: string, filePath?: string) {
    try {
        console.log(`[importImageFromBuffer] Importing "${name}" (${buffer.byteLength} bytes)`);
        
        const data = await importProjectBuffer(buffer, filePath || name) as any;
        if (!data || !data.layers) {
            throw new Error('Geçersiz dosya verisi veya katman bulunamadı.');
        }

        // Create a new document
        const doc = new ImageDocument(name, data.width, data.height);
        if (filePath) doc.filepath = filePath;
        
        // Hydrate layers: Convert serialized data to functional LayerClass instances
        const hydratedLayers = await Promise.all(data.layers.map(async (l: any, idx: number) => {
            const layer = new LayerClass(l.name, data.width, data.height);
            layer.visible = l.visible !== undefined ? l.visible : true;
            layer.opacity = l.opacity !== undefined ? l.opacity : 1.0;
            layer.blendMode = l.blendMode || 'source-over';
            layer.locked = !!l.locked;
            layer.type = l.type || 'raster';
            layer.shapes = l.shapes || [];
            
            if (l.canvas && layer.ctx) {
                if (l.canvas instanceof ImageData) {
                    layer.ctx.putImageData(l.canvas, 0, 0);
                } else {
                    layer.ctx.drawImage(l.canvas, 0, 0);
                }
            } else if (l.data && layer.ctx) {
                await new Promise((resolve) => {
                    const img = new Image();
                    img.onload = () => {
                        layer.ctx!.drawImage(img, 0, 0);
                        resolve(true);
                    };
                    img.onerror = () => resolve(false);
                    img.src = l.data;
                });
            }
            return layer;
        }));

        doc.layers = hydratedLayers;
        
        // Register and switch to the new document
        g.documents.push(doc);
        switchDocument(g.documents.length - 1);

        const el = document.getElementById('filePath');
        if (el && filePath) el.innerText = filePath;
        
        console.log(`[importImageFromBuffer] Document loaded: ${name}`);
        
        // Final UI refresh
        renderLayers();
        updateLayerPanel();

    } catch (error: any) {
        console.error('[importImageFromBuffer] Critical failed:', error);
        alert(`Dosya içe aktarılamadı: ${error.message}`);
    }
}

export async function handleSaveFile() {
    try {
        if (!g.filepath || g.filepath.toLowerCase().endsWith('.png') || g.filepath.toLowerCase().endsWith('.jpg') || g.filepath.toLowerCase().endsWith('.gif')) {
            // If no path or path is an image (can't save project as image directly), do Save As
            return handleSaveAsFile();
        }

        const content = await saveProjectToDataString(g.image_width, g.image_height, g.background_color || '#ffffff', layers);
        const savedPath = await api.saveFile(content, g.filepath, false, 'hcie');
        if (savedPath) {
            console.log('File saved to:', savedPath);
            g.filepath = savedPath;
            const el = document.getElementById('filePath');
            if (el) el.innerText = savedPath;
        }
    } catch (err) {
        console.error('Save failed:', err);
    }
}

export async function handleSaveAsFile() {
    try {
        // 1. Ask for Format
        const format = await (window as any).DialogHandler.showFormatSelector() as 'hcie' | 'png' | 'psd' | 'gif' | null;
        if (!format) return; // User cancelled

        let content: string | Uint8Array;
        let type: string = format;

        // 2. Export/Save using generalized routine
        const extension = "." + type;
        const outContent = await exportProjectBuffer(g.image_width, g.image_height, layers, extension);

        // 3. Save File
        const savedPath = await api.saveFile(outContent, g.filepath, true, type);
        if (savedPath) {
            console.log(`File saved as ${format} to:`, savedPath);
            if (type === 'hcie') {
                g.filepath = savedPath;
                const el = document.getElementById('filePath');
                if (el) el.innerText = savedPath;
            }
        }
    } catch (err) {
        console.error('Save As failed:', err);
    }
}

export async function handleExportFile() {
    try {
        const activeLayer = layers[g.activeLayerIndex];
        if (!activeLayer) return;

        const dataURL = (activeLayer.canvas as HTMLCanvasElement).toDataURL('image/png');
        await api.saveFile(dataURL, g.filepath, true, 'png');
    } catch (err) {
        console.error('Export failed:', err);
    }
}

// --- Edit Operations ---

export function undoImage() {
    if ((window as any).historyManager) {
        (window as any).historyManager.undo();
    } else {
        historyManager.undo(); // Fallback to singleton
    }
    renderLayers();
}

export function redoImage() {
    if ((window as any).historyManager) {
        (window as any).historyManager.redo();
    } else {
        historyManager.redo(); // Fallback to singleton
    }
    renderLayers();
}

/**
 * Clears the active layer. If a selection is active, only clears the selected area.
 * Otherwise, clears the entire layer.
 */
export function clearCanvas() {
    const activeLayer = layers[g.activeLayerIndex];
    if (!activeLayer || activeLayer.locked) return;

    const ctx = activeLayer.ctx as CanvasRenderingContext2D;
    
    // Check if there's an active selection
    const isSelectionActive = g.isSelectionActive && g.selectionCanvas;
    
    let oldData: ImageData;
    let newData: ImageData;
    
    if (isSelectionActive) {
        // Get selection bounds
        const selectionCanvas = g.selectionCanvas;
        if (!selectionCanvas) return;
        
        const selectionCtx = selectionCanvas.getContext('2d');
        if (!selectionCtx) return;
        
        const maskData = selectionCtx.getImageData(0, 0, selectionCanvas.width, selectionCanvas.height);
        
        // Find bounds of selection
        let minX = g.image_width, minY = g.image_height, maxX = 0, maxY = 0;
        let found = false;
        for (let y = 0; y < g.image_height; y++) {
            const rowOffset = y * g.image_width * 4;
            for (let x = 0; x < g.image_width; x++) {
                const alphaIndex = rowOffset + x * 4 + 3;
                if (maskData.data[alphaIndex] > 10) {
                    if (x < minX) minX = x;
                    if (x > maxX) maxX = x;
                    if (y < minY) minY = y;
                    if (y > maxY) maxY = y;
                    found = true;
                }
            }
        }
        
        if (!found) return; // No valid selection
        
        const width = maxX - minX + 1;
        const height = maxY - minY + 1;
        
        // Save old data for history (only the selected region)
        oldData = ctx.getImageData(minX, minY, width, height);
        
        // Clear only the selected area using destination-out composite
        ctx.save();
        ctx.globalCompositeOperation = 'destination-out';
        ctx.drawImage(selectionCanvas, minX, minY, width, height, minX, minY, width, height);
        ctx.restore();
        
        // Get new data for history
        newData = ctx.getImageData(minX, minY, width, height);
        
        // Create a DrawAction for the selected region only
        historyManager.push(new DrawAction(g.activeLayerIndex, oldData, newData));
    } else {
        // No selection - clear entire layer (original behavior)
        oldData = ctx.getImageData(0, 0, g.image_width, g.image_height);
        
        ctx.clearRect(0, 0, g.image_width, g.image_height);
        
        newData = ctx.getImageData(0, 0, g.image_width, g.image_height);
        historyManager.push(new DrawAction(g.activeLayerIndex, oldData, newData));
    }
    
    renderLayers();
    updateLayerPanel();
}

// --- Filter Operations ---

export function applyFilter(type: string) {
    const activeLayer = layers[g.activeLayerIndex];
    if (!activeLayer || activeLayer.locked) return;

    const ctx = activeLayer.ctx as CanvasRenderingContext2D;
    const oldData = ctx.getImageData(0, 0, g.image_width, g.image_height);
    const imageData = ctx.getImageData(0, 0, g.image_width, g.image_height);

    switch (type) {
        case 'negative':
        case 'invert':
            filters.applyNegative(imageData);
            break;
        case 'grayscale':
            filters.applyGrayscale(imageData);
            break;
        case 'sepia':
            filters.applySepia(imageData);
            break;
        case 'blur': {
            const blurred = filters.applyBoxBlur(imageData, g.blur_radius);
            ctx.putImageData(blurred, 0, 0);
            break;
        }
        case 'mosaic': {
            const pixelated = filters.applyMosaic(imageData, g.mosaic_block_size);
            ctx.putImageData(pixelated, 0, 0);
            break;
        }
        default:
            console.warn('Unknown filter type:', type);
            return;
    }

    if (type !== 'blur' && type !== 'mosaic') {
        ctx.putImageData(imageData, 0, 0);
    }

    const newData = ctx.getImageData(0, 0, g.image_width, g.image_height);
    historyManager.push(new DrawAction(g.activeLayerIndex, oldData, newData));

    renderLayers();
    updateLayerPanel();
}

// --- View Operations ---

export function zoomIn() {
    actualZoomIn(null);
}

export function zoomOut() {
    actualZoomOut(null);
}

// --- New Image Dialog ---

export function closeNewImageDialog() {
    const modal = document.getElementById('newImageModal');
    if (modal) modal.style.display = 'none';
}

const NIM_PRESETS = [
    { group: 'Blank', name: 'Custom',    w: 0,    h: 0,    key: 'custom' },
    { group: 'Blank', name: 'HD',        w: 1280, h: 720,  key: 'hd' },
    { group: 'Blank', name: 'Full HD',   w: 1920, h: 1080, key: 'fhd' },
    { group: 'Blank', name: '4K UHD',   w: 3840, h: 2160, key: '4k' },
    { group: 'Blank', name: 'Square',   w: 1024, h: 1024, key: 'square' },
    { group: 'Blank', name: 'A4',       w: 2480, h: 3508, key: 'a4' },
    { group: 'Icon',  name: '16×16',    w: 16,   h: 16,   key: 'icon16' },
    { group: 'Icon',  name: '32×32',    w: 32,   h: 32,   key: 'icon32' },
    { group: 'Icon',  name: '48×48',    w: 48,   h: 48,   key: 'icon48' },
    { group: 'Icon',  name: '256×256',  w: 256,  h: 256,  key: 'icon256' },
];

export function openNewImageDialog() {
    const modal = document.getElementById('newImageModal');
    if (!modal) return;
    modal.style.display = 'flex';

    // Pre-populate with responsive defaults from settings
    const sm = (window as any).settingsManager;
    const isMobile = window.innerWidth <= 768;
    const w = isMobile
        ? (sm?.get('canvas.defaultWidthMobile') ?? 512)
        : (sm?.get('canvas.defaultWidthDesktop') ?? 1280);
    const h = isMobile
        ? (sm?.get('canvas.defaultHeightMobile') ?? 512)
        : (sm?.get('canvas.defaultHeightDesktop') ?? 720);

    const wEl = document.getElementById('newWidth') as HTMLInputElement;
    const hEl = document.getElementById('newHeight') as HTMLInputElement;
    if (wEl) wEl.value = String(w);
    if (hEl) hEl.value = String(h);

    initNewImageDialog();
}

export function initNewImageDialog() {
    const list = document.getElementById('nimPresetList');
    if (!list) return; // Mobile layout — no preset panel

    list.innerHTML = '';
    const groups = [...new Set(NIM_PRESETS.map(p => p.group))];
    groups.forEach(group => {
        const header = document.createElement('div');
        header.className = 'nim-group-header';
        header.textContent = group;
        list.appendChild(header);

        NIM_PRESETS.filter(p => p.group === group).forEach(preset => {
            const card = document.createElement('div');
            card.className = 'nim-preset-card';
            card.dataset.key = preset.key;

            const thumb = document.createElement('div');
            thumb.className = 'nim-thumb';
            if (preset.w > 0 && preset.h > 0) {
                const ratio = preset.w / preset.h;
                const thumbW = ratio >= 1 ? 40 : Math.round(40 * ratio);
                const thumbH = ratio >= 1 ? Math.round(40 / ratio) : 40;
                thumb.style.width = thumbW + 'px';
                thumb.style.height = thumbH + 'px';
            } else {
                thumb.style.width = '32px';
                thumb.style.height = '32px';
                thumb.style.borderStyle = 'dashed';
            }

            const info = document.createElement('div');
            info.className = 'nim-card-info';
            const title = document.createElement('span');
            title.className = 'nim-card-name';
            title.textContent = preset.name;
            const dims = document.createElement('span');
            dims.className = 'nim-card-dims';
            dims.textContent = preset.w > 0 ? `${preset.w} × ${preset.h} px` : 'Custom size';
            info.appendChild(title);
            info.appendChild(dims);

            card.appendChild(thumb);
            card.appendChild(info);
            list.appendChild(card);

            card.onclick = () => {
                list.querySelectorAll('.nim-preset-card').forEach(c => c.classList.remove('selected'));
                card.classList.add('selected');
                if (preset.w > 0) {
                    const wEl = document.getElementById('newWidth') as HTMLInputElement;
                    const hEl = document.getElementById('newHeight') as HTMLInputElement;
                    if (wEl) wEl.value = String(preset.w);
                    if (hEl) hEl.value = String(preset.h);
                }
            };
        });
    });

    // Select the card matching current width/height
    const wEl = document.getElementById('newWidth') as HTMLInputElement;
    const hEl = document.getElementById('newHeight') as HTMLInputElement;
    const curW = parseInt(wEl?.value || '0');
    const curH = parseInt(hEl?.value || '0');
    const match = NIM_PRESETS.find(p => p.w === curW && p.h === curH);
    if (match) {
        const card = list.querySelector(`[data-key="${match.key}"]`);
        if (card) card.classList.add('selected');
    }
}

export function filterNimPresets(query: string) {
    const list = document.getElementById('nimPresetList');
    if (!list) return;
    const q = query.toLowerCase().trim();
    list.querySelectorAll('.nim-preset-card').forEach((card: Element) => {
        const name = (card.querySelector('.nim-card-name') as HTMLElement)?.textContent?.toLowerCase() || '';
        (card as HTMLElement).style.display = (!q || name.includes(q)) ? '' : 'none';
    });
    list.querySelectorAll('.nim-group-header').forEach((hdr: Element) => {
        // Hide group header if all cards in its group are hidden
        let next = hdr.nextElementSibling;
        let anyVisible = false;
        while (next && !next.classList.contains('nim-group-header')) {
            if ((next as HTMLElement).style.display !== 'none') anyVisible = true;
            next = next.nextElementSibling;
        }
        (hdr as HTMLElement).style.display = anyVisible ? '' : 'none';
    });
}

export function applyPreset() {
    const preset = (document.getElementById('imagePreset') as HTMLSelectElement).value;
    const widthInput = document.getElementById('newWidth') as HTMLInputElement;
    const heightInput = document.getElementById('newHeight') as HTMLInputElement;

    switch (preset) {
        case 'hd':
            widthInput.value = '1280';
            heightInput.value = '720';
            break;
        case 'fhd':
            widthInput.value = '1920';
            heightInput.value = '1080';
            break;
        case '4k':
            widthInput.value = '3840';
            heightInput.value = '2160';
            break;
        case 'a4':
            widthInput.value = '2480';
            heightInput.value = '3508';
            break;
        case 'square':
            widthInput.value = '1024';
            heightInput.value = '1024';
            break;
        case 'icon16':
            widthInput.value = '16';
            heightInput.value = '16';
            break;
        case 'icon32':
            widthInput.value = '32';
            heightInput.value = '32';
            break;
        case 'icon48':
            widthInput.value = '48';
            heightInput.value = '48';
            break;
        case 'icon256':
            widthInput.value = '256';
            heightInput.value = '256';
            break;
        case 'custom':
            // Don't change values
            break;
    }
}

export function setOrientation(orientation: 'portrait' | 'landscape') {
    const widthInput = document.getElementById('newWidth') as HTMLInputElement;
    const heightInput = document.getElementById('newHeight') as HTMLInputElement;
    const width = parseInt(widthInput.value);
    const height = parseInt(heightInput.value);

    const btnPortrait = document.getElementById('btn-portrait');
    const btnLandscape = document.getElementById('btn-landscape');

    if (orientation === 'portrait') {
        if (width > height) {
            widthInput.value = height.toString();
            heightInput.value = width.toString();
        }
        btnPortrait?.classList.add('btn-active');
        btnLandscape?.classList.remove('btn-active');
    } else {
        if (height > width) {
            widthInput.value = height.toString();
            heightInput.value = width.toString();
        }
        btnLandscape?.classList.add('btn-active');
        btnPortrait?.classList.remove('btn-active');
    }
}

export function createNewImage() {
    const width = parseInt((document.getElementById('newWidth') as HTMLInputElement).value);
    const height = parseInt((document.getElementById('newHeight') as HTMLInputElement).value);
    const nameEl = document.getElementById('newDocName') as HTMLInputElement;
    const name = nameEl?.value?.trim() || 'Untitled';
    
    const bgColor = (document.getElementById('newBgColor') as HTMLInputElement).value;
    const isTransparent = (document.getElementById('transparentBg') as HTMLInputElement).checked;

    newDocument(name, width, height);

    // After newDocument, the active layer is the Background layer.
    const activeLayer = layers[g.activeLayerIndex];
    if (activeLayer) {
        const ctx = activeLayer.ctx as CanvasRenderingContext2D;
        ctx.clearRect(0, 0, width, height);
        if (!isTransparent) {
            ctx.fillStyle = bgColor;
            ctx.fillRect(0, 0, width, height);
        }
    }
    
    closeNewImageDialog();
    renderLayers();
    updateLayerPanel();
}

export function exitApp() {
    if ((window as any).__TAURI__) {
        // Tauri exit
        try {
            (window as any).__TAURI__.process.exit(0);
        } catch (e) {
            console.error('Tauri exit failed:', e);
            window.close();
        }
    } else if ((window as any).electronAPI) {
        // Electron exit via API if available
        window.close();
    } else {
        // Web fallback
        if (confirm("Are you sure you want to exit? Unsaved changes will be lost.")) {
            window.location.href = "about:blank";
        }
    }
}

// Expose to window
if (typeof window !== 'undefined') {
    Object.assign(window, {
        handleOpenFile,
        openImage: handleOpenFile, // Alias for legacy HTML
        handleSaveFile,
        saveProject: handleSaveFile, // Alias for legacy HTML
        handleSaveAsFile,
        saveAsProject: handleSaveAsFile, // Alias for legacy HTML
        handleExportFile,
        undoImage,
        redoImage,
        clearCanvas,
        applyFilter,
        zoomIn,
        zoomOut,
        closeNewImageDialog,
        openNewImageDialog,
        newCanvas: openNewImageDialog, // Alias for legacy HTML
        exitApp,
        applyPreset,
        setOrientation,
        createNewImage,
        initNewImageDialog,
        filterNimPresets,
        importImageFromBuffer
    });
}